<template>
    <v-card-text>
        <v-row>
            <v-col cols="12" sm="3">
                <v-label>Группировка</v-label>
                <v-select
                    v-model="groupBy"
                    item-value="id"
                    item-text="text"
                    :items="groupByOptions">
                </v-select>
            </v-col>
            <v-col cols="12" sm="3">
                <v-label>Сравнить с периодом</v-label>
                <DateField
                    :range="true"
                    v-model="periodB"
                    clearable>
                </DateField>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <div class="funnel-row">
                    <div class="funnel-item">
                        <FunnelChart
                            title="Текущий период"
                            :stages="funnelA.stages"
                            :contract-amount="funnelA.contractAmount"
                            :loading="loading">
                        </FunnelChart>
                    </div>
                    <div class="funnel-item" v-if="hasPeriodB">
                        <FunnelChart
                            title="Период сравнения"
                            :stages="funnelB.stages"
                            :contract-amount="funnelB.contractAmount"
                            :loading="loadingB">
                        </FunnelChart>
                    </div>
                </div>
            </v-col>
        </v-row>
        <v-row v-if="loading">
            <v-col cols="12" md="7">
                <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
            </v-col>
        </v-row>
        <div v-if="!loading" style="overflow: auto; border-left: thin solid rgba(255, 255, 255, 0.18) !important;">
            <v-data-table
                :headers="headers"
                :items="items"
                hide-default-footer
                disable-pagination
                class="table__lined">
                <template v-slot:body="{ items }">
                    <tbody>
                    <tr v-for="(item, i) in items" :key="i">
                        <td>{{ groupLabel(item) }}</td>
                        <td>{{ item.totalLeads }}</td>
                        <td>{{ item.callLeads }}</td>
                        <td>{{ item.robotLeads }}</td>
                        <td>{{ item.chatLeads }}</td>
                        <td>{{ item.formLeads }}</td>
                        <td>
                            {{ item.withProblemFilled }}
                            (<span v-html="percentHtml(item.problemFilledRate)"></span>)
                        </td>
                        <td>{{ item.notReached }}</td>
                        <td>
                            {{ item.scheduled }}
                            (<span v-html="percentHtml(item.scheduledRate)"></span>)
                        </td>
                        <td>
                            {{ item.came }}
                            (<span v-html="percentHtml(item.cameRate)"></span>)
                        </td>
                        <td>{{ item.notCame }}</td>
                        <td>
                            {{ item.rejected }}
                            (<span v-html="percentHtml(item.rejectedRate, true)"></span>)
                        </td>
                        <td>
                            {{ item.hasClient }}
                            (<span v-html="percentHtml(item.clientRate)"></span>)
                        </td>
                        <td>
                            {{ item.contracts }}
                            (<span v-html="percentHtml(item.contractRate)"></span>)
                        </td>
                        <td>{{ item.contractAmount.toLocaleString('ru-RU') }} ₽</td>
                    </tr>
                    </tbody>
                </template>
            </v-data-table>
        </div>
    </v-card-text>
</template>

<script>
import ReportService from "@/_services/ReportService";
import {reportHelper} from "@/_helpers/report/reportHelper";
import DateField from "@/_chunks/DateField";
import FunnelChart from "./FunnelChart";

const LEAD_TYPE_LABELS = {
    CALL: 'Звонок',
    ROBOT: 'Робот',
    CHAT: 'Чат',
    FORM: 'Форма'
};

// Ordered funnel stages shown in FunnelChart - later stages are always subsets of earlier ones.
function toFunnelStages(items) {
    const totals = items.reduce((acc, item) => {
        acc.totalLeads += item.totalLeads;
        acc.scheduled += item.scheduled;
        acc.came += item.came;
        acc.hasClient += item.hasClient;
        acc.contracts += item.contracts;
        acc.contractAmount += item.contractAmount;
        return acc;
    }, {totalLeads: 0, scheduled: 0, came: 0, hasClient: 0, contracts: 0, contractAmount: 0});
    return {
        stages: [
            {name: 'Заявки', value: totals.totalLeads},
            {name: 'Записан', value: totals.scheduled},
            {name: 'Пришел', value: totals.came},
            {name: 'Клиенты', value: totals.hasClient},
            {name: 'Договоры', value: totals.contracts}
        ],
        contractAmount: totals.contractAmount
    };
}

export default {
    name: 'ReportFunnel',
    components: {DateField, FunnelChart},
    props: {
        filter: Object,
    },
    data: function () {
        return {
            loading: false,
            loadingB: false,
            items: [],
            itemsB: [],
            periodB: null,
            groupBy: 'SOURCE',
            groupByOptions: [
                {id: 'SOURCE', text: 'Источник'},
                {id: 'OPERATOR', text: 'Оператор'},
                {id: 'AREA', text: 'Область права'},
                {id: 'CITY', text: 'Город'},
                {id: 'LEAD_TYPE', text: 'Канал обращения'}
            ],
            headers: [
                {text: 'Группа', value: 'groupLabel', sortable: false},
                {text: 'Всего лидов', value: 'totalLeads'},
                {text: 'Звонок', value: 'callLeads'},
                {text: 'Робот', value: 'robotLeads'},
                {text: 'Чат', value: 'chatLeads'},
                {text: 'Форма', value: 'formLeads'},
                {text: 'Проблема заполнена', value: 'withProblemFilled'},
                {text: 'Не дозвонились', value: 'notReached'},
                {text: 'Назначена встреча', value: 'scheduled'},
                {text: 'Пришли', value: 'came'},
                {text: 'Не пришли', value: 'notCame'},
                {text: 'Брак/шлак', value: 'rejected'},
                {text: 'Клиенты', value: 'hasClient'},
                {text: 'Договоров', value: 'contracts'},
                {text: 'Сумма договоров', value: 'contractAmount'}
            ]
        }
    },
    computed: {
        hasPeriodB() {
            return Array.isArray(this.periodB) && this.periodB.length === 2;
        },
        funnelA() {
            return toFunnelStages(this.items);
        },
        funnelB() {
            return toFunnelStages(this.itemsB);
        }
    },
    watch: {
        groupBy() {
            this.fetch();
            if (this.hasPeriodB) {
                this.fetchB();
            }
        },
        periodB() {
            if (this.hasPeriodB) {
                this.fetchB();
            } else {
                this.itemsB = [];
            }
        }
    },
    methods: {
        fetch() {
            this.loading = true;
            ReportService.getFunnelReport({...this.filter, groupBy: this.groupBy}).then(report => {
                this.items = report.items;
                this.loading = false;
            });
        },
        fetchB() {
            this.loadingB = true;
            const dates = [...this.periodB].sort();
            ReportService.getFunnelReport({dates, groupBy: this.groupBy}).then(report => {
                this.itemsB = report.items;
                this.loadingB = false;
            });
        },
        groupLabel(item) {
            if (this.groupBy === 'LEAD_TYPE') {
                return LEAD_TYPE_LABELS[item.groupLabel] || item.groupLabel;
            }
            return item.groupLabel;
        },
        percentHtml(rate, invert = false) {
            const percentValue = Math.round((rate * 100 + Number.EPSILON) * 100) / 100;
            const options = invert ? {
                colors: {green: [0, 19], yellow: [20, 25], red: [26, 100]}
            } : reportHelper.contractStatOptions;
            const statColor = reportHelper.getStatColor(percentValue, options);
            return `<span class="${statColor}">${percentValue}%</span>`;
        }
    },
    created() {
        this.fetch();
    }
};
</script>

<style scoped>
.funnel-row {
    display: flex;
    flex-wrap: wrap;
    gap: 50px;
}

.funnel-item {
    flex: 0 1 600px;
    max-width: 600px;
    width: 100%;
}
</style>
